"use client";
import { useState } from "react";
import { Spinner } from "@/components/ui/Spinner";
import { Drawer } from "@/components/ui/Drawer";
import { CardCollectSection } from "./CardCollectSection";
import {
  PatientDetailsProvider,
  usePatientDetailsContext,
} from "../../context/PatientDetailsContext";
import { PatientAvatar } from "./PatientAvatar";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  PatientDrawerTitle,
  PatientDrawerTitleSkeleton,
} from "./PatientDrawerTitle";
import { PatientMeta, PatientMetaSkeleton } from "../PatientMeta";

interface PatientDetailsDrawerProps {
  patientId: string | null;
  onClose: () => void;
}

function PatientDetailsDrawerInner({
  onClose,
}: Omit<PatientDetailsDrawerProps, "patientId">) {
  const { patientId, patient, isLoading } = usePatientDetailsContext();

  const [linkSent, setLinkSent] = useState(false);

  const fullName = patient
    ? `${patient.firstName ?? ""} ${patient.lastName ?? ""}`.trim()
    : "";

  const handleClose = () => {
    setLinkSent(false);
    onClose();
  };

  return (
    <>
      <Drawer
        open={patientId !== null}
        onClose={handleClose}
        header={{
          start: (
            <Skeleton active={isLoading}>
              <PatientAvatar fullName={fullName} />
            </Skeleton>
          ),
          title: isLoading ? (
            <PatientDrawerTitleSkeleton />
          ) : (
            <PatientDrawerTitle>{fullName}</PatientDrawerTitle>
          ),
          subtitle:
            isLoading || !patient ? (
              <PatientMetaSkeleton />
            ) : (
              <PatientMeta patient={patient} />
            ),
        }}
      >
        {isLoading && (
          <div className="flex flex-1 items-center justify-center">
            <Spinner />
          </div>
        )}

        {patient && !isLoading && <CardCollectSection linkSent={linkSent} />}
      </Drawer>
    </>
  );
}

export function PatientDetailsDrawer({
  patientId,
  onClose,
}: PatientDetailsDrawerProps) {
  return (
    <PatientDetailsProvider patientId={patientId} onError={onClose}>
      <PatientDetailsDrawerInner onClose={onClose} />
    </PatientDetailsProvider>
  );
}
